import { type ComponentProps } from 'react'
import { ArrowUpRight } from 'lucide-react'
import { twMerge } from 'tailwind-merge'
import { Button } from '@highlight/ui'

interface SidepanelSectionViewAllProps extends Omit<ComponentProps<'button'>, 'type'> {}

/**
 * The header's trailing "View all" action — revealed with the header's hover
 * pill, hidden otherwise.
 *
 * Absolutely positioned rather than placed in the header's flex flow so it
 * never competes for space with a section's subtitle slot (Upcoming's loading
 * line, Outbox's subheader), which occupies the same right edge. `right-3`
 * mirrors the header's own `px-3`.
 */
export function SidepanelSectionViewAll({ className, children = 'View all', ...props }: SidepanelSectionViewAllProps) {
  return (
    <Button
      type="button"
      variant="ghost"
      size="xs"
      right={
        <ArrowUpRight
          strokeWidth={2}
          className="size-3.5 shrink-0 transition-transform group-hover/view-all:-translate-y-px group-hover/view-all:translate-x-px motion-reduce:transition-none"
        />
      }
      className={twMerge(
        'group/view-all absolute inset-y-0 right-3 my-auto',
        // The header is 28px and the button 20px; the pseudo grows the target to the header's full
        // height. The pseudo's right corners match the header's own radius, so the area's outer
        // corners sit on the pill's edge instead of squaring it off. `-left-2` keeps the inner edge
        // off the label itself, so the target doesn't stop flush against the first glyph.
        "after:corner-squircle after:absolute after:-inset-y-1 after:-left-2 after:-right-3 after:rounded-r-[var(--radius-size-md)] after:content-['']",
        // Hover-only, but focus-within keeps it reachable by keyboard: the button stays in the tab
        // order while transparent, so it has to reveal itself when focus lands on it.
        'opacity-0',
        'group-focus-within/section-header:opacity-100 group-hover/section-header:opacity-100',
        className,
      )}
      {...props}
    >
      {children}
    </Button>
  )
}
